import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  NetworkStatus,
  type DefaultOptions,
  type MutationFunctionOptions,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

interface DateObject {
  [name: string]: DateObject | Date;
}

interface NetworkLikeError {
  statusCode?: number;
  status?: number;
  response?: { status?: number };
}

function resolveNetworkStatus(networkLikeError: NetworkLikeError): number | undefined {
  if (networkLikeError.statusCode !== undefined) {
    return networkLikeError.statusCode;
  }
  if (networkLikeError.status !== undefined) {
    return networkLikeError.status;
  }
  if (networkLikeError.response?.status !== undefined) {
    return networkLikeError.response.status;
  }
  return undefined;
}

const dateConversionLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const { data } = response;
    const convertDateTimeFields = (obj: DateObject) => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          if (typeof obj[key] === 'object' && obj[key] !== null && !(obj[key] instanceof Date)) {
            // If the current property is an object, recurse into it
            convertDateTimeFields(obj[key]);
          } else if (typeof obj[key] === 'string') {
            // If the current property is a string, check if it matches the ISO 8601 date-time pattern
            const iso8601Pattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
            if (iso8601Pattern.test(obj[key])) {
              // If it matches, convert it to a Date object
              obj[key] = new Date(obj[key]);
            }
          }
        }
      }
    };

    if (data) {
      convertDateTimeFields(data);
    }

    return response;
  });
});

const createErrorLink = (onUnauthorized?: () => void) =>
  onError(({ graphQLErrors, networkError }) => {
    let unauthorized = false;

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Code: ${extensions?.code}`,
        );
        if (extensions?.status === 401) {
          unauthorized = true;
        }
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
      const networkLikeError = networkError as NetworkLikeError;
      const status = resolveNetworkStatus(networkLikeError);
      if (status === 401) {
        unauthorized = true;
      }
    }

    if (unauthorized && onUnauthorized) {
      onUnauthorized();
    }
  });

interface AuthOptions {
  token?: string;
  onUnauthorized?: () => void;
}

function createApolloClient(
  apiUrl: string,
  defaultOptions?: DefaultOptions,
  authOptions?: AuthOptions,
) {
  const { token, onUnauthorized } = authOptions || {};
  const httpLink = new HttpLink({
    uri: apiUrl,
    credentials: 'include',
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }));

    return forward(operation);
  });

  const errorLink = createErrorLink(onUnauthorized);

  return new ApolloClient({
    link: from([errorLink, authLink, dateConversionLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions,
  });
}

export { ApolloError, ApolloProvider, createApolloClient, NetworkStatus };
export type { MutationFunctionOptions };
